import { UNAVAILABLE_RULE_ID } from './appointment';
import type { GuideChatProduct, UnavailableResponse } from './types';
import { hasContactForm, isGuideChatProduct, outageProduct } from './types';
import { conversationTokenFor } from './session';

export const CONTACT_PROXY = '/api/poc-guide-chat/contact';

// Mirrors the backend labels for when the backend could not send them, since it may be the part that is down.
export const CONTACT_TEXTS = {
  badge: 'Message à un conseiller',
  firstName: 'Prénom',
  lastName: 'Nom',
  email: 'E-mail',
  message: 'Votre message',
  messagePlaceholder: 'Votre message pour le conseiller (aucune donnée sensible)',
  submit: 'Envoyer ma demande',
  sending: 'Envoi…',
  confirmed: "C'est envoyé : un conseiller Réassurez-moi vous répond par e-mail dans les meilleurs délais.",
  doneTitle: 'Message transmis à Réassurez-moi',
  missingFields: 'Prénom, nom, e-mail et message sont requis.',
  invalidEmail: 'Votre e-mail est invalide.',
  // Same wording as the /contact page's consent box.
  consent: "J'accepte d'être contacté(e) par email par Réassurez-moi afin de traiter ma demande.",
  missingConsent: 'Veuillez accepter pour pouvoir envoyer votre demande.',
  failed: "Votre message n'a pas pu être envoyé.",
} as const;

export interface ContactMessageFields {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

export const EMPTY_CONTACT_MESSAGE: ContactMessageFields = { firstName: '', lastName: '', email: '', message: '' };

export interface ContactCardData {
  kind: 'contact';
  primary?: boolean;
  title?: string;
  message?: string;
  nonAnswerReason?: string | null;
  nonAnswerReasonLabel?: string | null;
  product: GuideChatProduct;
  subject?: string;
  subjectLabel?: string;
  messagePlaceholder?: string;
  submitLabel?: string;
  confirmation?: string;
  handover: Record<string, unknown>;
}

export interface ContactBody extends ContactMessageFields {
  product: GuideChatProduct;
  handover: Record<string, unknown>;
  conversationToken?: string;
}

export type ContactResult = { ok: true; confirmation: string | null; reference: string | null } | { ok: false; error: string };

// The 503 body carries more than the shared type declares; api.ts spreads it whole, so these fields survive.
export interface UnavailableWithContact extends UnavailableResponse {
  conversationId?: string;
  contact?: Partial<ContactCardData> | null;
}

export function contactCardOf(value: unknown): ContactCardData | null {
  if (!value || typeof value !== 'object') return null;
  const card = value as Partial<ContactCardData>;
  if (card.kind !== 'contact' || !isGuideChatProduct(card.product) || !hasContactForm(card.product)) return null;
  return { ...card, kind: 'contact', product: card.product, handover: card.handover && typeof card.handover === 'object' ? card.handover : {} };
}

// The form shown when the assistant is down: the question and the conversation id are all the context there is.
export function unavailableContactOf(
  info: UnavailableWithContact,
  fallback: { conversationId?: string | null; question?: string | null; product?: GuideChatProduct | null },
): ContactCardData | null {
  const product = outageProduct(info, fallback.product);
  if (!hasContactForm(product)) return null;
  const sent = contactCardOf(info.contact);
  const contact = sent?.product === product ? sent : outageContactCard(product);
  const handover: Record<string, unknown> = { ...contact.handover };
  if (!handover.conversationId && (fallback.conversationId || info.conversationId)) handover.conversationId = fallback.conversationId || info.conversationId;
  if (!handover.question && fallback.question) handover.question = fallback.question;
  return { ...contact, handover };
}

// A timeout or a dead backend sends no form: travel and prévoyance still offer the e-mail one.
function outageContactCard(product: GuideChatProduct): ContactCardData {
  return { kind: 'contact', product, handover: { product, outcome: 'appointment', ruleId: UNAVAILABLE_RULE_ID } };
}

export function missingContactMessageFields(fields: ContactMessageFields): (keyof ContactMessageFields)[] {
  return (Object.keys(EMPTY_CONTACT_MESSAGE) as (keyof ContactMessageFields)[]).filter((f) => !fields[f].trim());
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

export function contactBody(fields: ContactMessageFields, product: GuideChatProduct, handover: Record<string, unknown>): ContactBody {
  return {
    firstName: fields.firstName.trim(),
    lastName: fields.lastName.trim(),
    email: fields.email.trim(),
    message: fields.message.trim(),
    product,
    handover,
  };
}

export async function postContact(body: ContactBody): Promise<ContactResult> {
  try {
    const conversationToken = body.conversationToken ?? conversationTokenFor(body.handover.conversationId);
    const res = await fetch(CONTACT_PROXY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(conversationToken ? { ...body, conversationToken } : body),
    });
    const data = (await res.json().catch(() => null)) as { error?: string; confirmation?: string; contact?: { reference?: string } } | null;
    if (!res.ok || !data || data.error) return { ok: false, error: data?.error || CONTACT_TEXTS.failed };
    return { ok: true, confirmation: typeof data.confirmation === 'string' ? data.confirmation : null, reference: data.contact?.reference ?? null };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : CONTACT_TEXTS.failed };
  }
}
